import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  AlertTriangle, 
  Tag, 
  Info, 
  ChevronRight,
  Megaphone,
  Calendar,
  ArrowLeft
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import { Announcement, AnnouncementType } from '../types';

const AnnouncementsPage: React.FC = () => {
  const { businessId, currentBusiness } = useBusiness();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | AnnouncementType>('all');

  // Cargar anuncios
  useEffect(() => {
    const loadAnnouncements = async () => {
      if (!businessId) return;

      try {
        const q = query(
          collection(db, 'announcements'),
          where('businessId', '==', businessId),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const now = new Date();
        
        const data = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date()
          }))
          .filter((ann: any) => {
            // Filtrar expirados
            if (ann.expirationDate && new Date(ann.expirationDate) < now) {
              return false;
            }
            // Filtrar por audiencia
            if (ann.targetAudience === 'members_only') {
              const hasMembership = currentUser?.activeMembership?.isActive || 
                                   (currentUser?.activePass && currentUser.activePass.creditsRemaining > 0);
              if (!hasMembership) return false;
            }
            return true;
          }) as Announcement[];

        setAnnouncements(data);
      } catch (error) {
        console.error('Error cargando anuncios:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnnouncements();
  }, [businessId, currentUser]);

  // Config por tipo
  const getTypeConfig = (type: AnnouncementType) => {
    switch (type) {
      case 'urgent':
        return { 
          icon: <AlertTriangle className="w-5 h-5" />, 
          bgClass: 'bg-gradient-to-br from-red-600/20 to-rose-600/20 border-red-500/50',
          headerBg: 'bg-gradient-to-r from-red-600 to-rose-600',
          iconBg: 'bg-red-500/20',
          iconColor: 'text-red-400',
          buttonBg: 'bg-red-500 hover:bg-red-600',
          label: 'Urgente'
        };
      case 'promo':
        return { 
          icon: <Tag className="w-5 h-5" />, 
          bgClass: 'bg-gradient-to-br from-amber-600/20 to-orange-600/20 border-amber-500/50',
          headerBg: 'bg-gradient-to-r from-amber-500 to-orange-500',
          iconBg: 'bg-amber-500/20',
          iconColor: 'text-amber-400',
          buttonBg: 'bg-amber-500 hover:bg-amber-600',
          label: 'Promoción'
        };
      case 'info':
      default:
        return { 
          icon: <Info className="w-5 h-5" />, 
          bgClass: 'bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border-blue-500/50',
          headerBg: 'bg-gradient-to-r from-blue-500 to-cyan-500',
          iconBg: 'bg-blue-500/20',
          iconColor: 'text-blue-400',
          buttonBg: 'bg-blue-500 hover:bg-blue-600',
          label: 'Información'
        };
    }
  };

  // Filtrar anuncios
  const filteredAnnouncements = filter === 'all' 
    ? announcements 
    : announcements.filter(a => a.type === filter);

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Novedades - {currentBusiness?.name || 'Mi Gimnasio'}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-950 -m-4 sm:-m-6 lg:-m-8 p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>

          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl">
              <Megaphone className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Novedades</h1>
              <p className="text-gray-400">Mantente al día con las últimas noticias</p>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { value: 'all', label: 'Todos', icon: <Megaphone className="w-4 h-4" /> },
              { value: 'urgent', label: 'Urgentes', icon: <AlertTriangle className="w-4 h-4" /> },
              { value: 'promo', label: 'Promociones', icon: <Tag className="w-4 h-4" /> },
              { value: 'info', label: 'Información', icon: <Info className="w-4 h-4" /> }
            ].map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  filter === f.value
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {f.icon}
                {f.label}
              </button>
            ))}
          </div>

          {/* Lista de anuncios */}
          {filteredAnnouncements.length === 0 ? (
            <div className="text-center py-16 bg-gray-900/50 rounded-2xl border border-gray-800">
              <Megaphone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">No hay novedades</h3>
              <p className="text-gray-400">
                {filter === 'all' 
                  ? 'No hay anuncios activos en este momento'
                  : `No hay anuncios de tipo "${getTypeConfig(filter as AnnouncementType).label}"`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAnnouncements.map((announcement) => {
                const config = getTypeConfig(announcement.type);
                
                return (
                  <div 
                    key={announcement.id}
                    className={`rounded-2xl border overflow-hidden ${config.bgClass}`}
                  >
                    {/* Image */}
                    {announcement.imageUrl && (
                      <div className="h-48 sm:h-64 overflow-hidden">
                        <img 
                          src={announcement.imageUrl} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="p-5 sm:p-6">
                      {/* Type & Date */}
                      <div className="flex items-center justify-between mb-4">
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${config.iconBg}`}>
                          <span className={config.iconColor}>{config.icon}</span>
                          <span className={`text-xs font-medium ${config.iconColor} uppercase`}>
                            {config.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(announcement.createdAt)}
                        </div>
                      </div>
                      
                      {/* Title & Body */}
                      <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
                        {announcement.title}
                      </h2>
                      <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {announcement.body}
                      </p>
                      
                      {/* CTA */}
                      {announcement.ctaText && announcement.ctaLink && (
                        <button
                          onClick={() => navigate(announcement.ctaLink!)}
                          className={`mt-6 px-6 py-3 ${config.buttonBg} text-white font-medium rounded-xl transition-colors flex items-center gap-2`}
                        >
                          {announcement.ctaText}
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      )}

                      {/* Expiration */}
                      {announcement.expirationDate && (
                        <p className="text-xs text-gray-500 mt-4">
                          Válido hasta: {new Date(announcement.expirationDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AnnouncementsPage;




















